import { Dropdown, MenuProps, Modal } from 'antd';
import React, { ReactElement, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import IApplicationPropertyValuer from '~Api/Application/IApplicationPropertyValuer';
import ValuerStatusEnum from '~Api/Application/ValuerStatusEnum';
import {
    applicationPropertyValuerCancelledAction,
    applicationPropertyValuerRefusedAction,
} from '~Applications/actions';
import IValuer from '~Valuers/IValuer';
import EnterQuoteModal from './EnterQuoteModal';
import EnterValuationModal from './EnterValuationModal';
import QuoteRequestedModal from './QuoteRequestedModal';
import ValuationOrderedModal from './ValuationOrderedModal';

export default function PropertyValuerActions(props: {
    application: IApplication;
    applicationProperty: IApplicationProperty;
    valuation: IApplicationPropertyValuer;
    valuer: IValuer;
}): ReactElement {
    const {
        application,
        applicationProperty,
        valuation,
        valuer,
    } = props;

    const dispatch: Dispatch = useDispatch();

    const [ isQuoteReceivedModalOpen, setIsQuoteReceivedModalOpen ] = useState<boolean>(false);
    const [ isQuoteRequestedModalOpen, setIsQuoteRequestedModalOpen ] = useState<boolean>(false);
    const [ isValuationOrderedModalOpen, setIsValuationOrderedModalOpen ] = useState<boolean>(false);
    const [ isValuationReceivedModalOpen, setIsValuationReceivedModalOpen ] = useState<boolean>(false);

    const onClickEnterQuote: () => void = useCallback((): void => {
        setIsQuoteReceivedModalOpen(true);
    }, []);

    const onClickEnterQuoteCancel: () => void = useCallback((): void => {
        setIsQuoteReceivedModalOpen(false);
    }, []);

    const onClickEnterValuation: () => void = useCallback((): void => {
        setIsValuationReceivedModalOpen(true);
    }, []);

    const onClickEnterValuationCancel: () => void = useCallback((): void => {
        setIsValuationReceivedModalOpen(false);
    }, []);

    const onClickQuoteRequested: () => void = useCallback((): void => {
        setIsQuoteRequestedModalOpen(true);
    }, []);

    const onClickQuoteRequestedCancel: () => void = useCallback((): void => {
        setIsQuoteRequestedModalOpen(false);
    }, []);

    const onClickValuationOrdered: () => void = useCallback((): void => {
        setIsValuationOrderedModalOpen(true);
    }, []);

    const onClickValuationOrderedCancel: () => void = useCallback((): void => {
        setIsValuationOrderedModalOpen(false);
    }, []);

    const onClickCancelValuationCallback: () => void = useCallback((): void => {
        Modal.confirm({
            content: 'Are you sure you want to cancel this property valuation?',
            onOk: (): void => {
                dispatch(applicationPropertyValuerCancelledAction(
                    applicationProperty.uuid,
                    valuation.uuid,
                ));
            },
            title: 'Cancel Property Valuation',
        });
    }, [
        applicationProperty.uuid,
        dispatch,
        valuation,
    ]);

    const onClickRefuseValuationCallback: () => void = useCallback((): void => {
        Modal.confirm({
            content: 'Are you sure you want to mark this property valuation as refused?',
            okText: 'Mark as Refused',
            onOk: (): void => {
                dispatch(applicationPropertyValuerRefusedAction(
                    applicationProperty.uuid,
                    valuation.uuid,
                ));
            },
            title: 'Mark as Refused',
        });
    }, [
        applicationProperty.uuid,
        dispatch,
        valuation,
    ]);

    const showQuoteRequestedMenuItem: boolean = (
        !valuation || !valuation.status || [
            ValuerStatusEnum.QuoteRequested,
            ValuerStatusEnum.QuoteReceived,
            ValuerStatusEnum.ValuationOrdered,
            ValuerStatusEnum.ValuationReceived,
            ValuerStatusEnum.Cancelled,
            ValuerStatusEnum.Refused,
        ].includes(valuation.status)
    );

    const showEnterQuoteMenuItem: boolean = (
        !valuation || !valuation.status || [
            ValuerStatusEnum.QuoteReceived,
            ValuerStatusEnum.ValuationOrdered,
            ValuerStatusEnum.ValuationReceived,
            ValuerStatusEnum.Cancelled,
            ValuerStatusEnum.Refused,
        ].includes(valuation.status)
    );

    const showValuationOrderedMenuItem: boolean = (
        !valuation || !valuation.status || [
            ValuerStatusEnum.QuoteRequested,
            ValuerStatusEnum.ValuationOrdered,
            ValuerStatusEnum.ValuationReceived,
            ValuerStatusEnum.Cancelled,
            ValuerStatusEnum.Refused,
        ].includes(valuation.status)
    );

    const showEnterValuationMenuItem: boolean = (
        !valuation || !valuation.status || [
            ValuerStatusEnum.QuoteReceived,
            ValuerStatusEnum.QuoteRequested,
            ValuerStatusEnum.Cancelled,
            ValuerStatusEnum.Refused,
        ].includes(valuation.status)
    );

    const showCancelValuationMenuItem: boolean = !!(
        valuation && [
            ValuerStatusEnum.QuoteReceived,
            ValuerStatusEnum.QuoteRequested,
            ValuerStatusEnum.ValuationOrdered,
            ValuerStatusEnum.Refused,
        ].includes(valuation.status)
    );

    const showRefuseValuationMenuItem: boolean = !!(
        valuation && [
            ValuerStatusEnum.QuoteReceived,
            ValuerStatusEnum.QuoteRequested,
            ValuerStatusEnum.ValuationOrdered,
            ValuerStatusEnum.Cancelled,
        ].includes(valuation.status)
    );

    const workflowMenu: MenuProps = {
        items: [
            ...(showQuoteRequestedMenuItem ? [
                {
                    key: 'quote-requested',
                    label: 'Quote Requested',
                    onClick: onClickQuoteRequested,
                },
            ] : []),
            ...(showEnterQuoteMenuItem ? [
                {
                    key: 'enter-quote',
                    label: 'Enter Quote',
                    onClick: onClickEnterQuote,
                },
            ] : []),
            ...(showValuationOrderedMenuItem ? [
                {
                    key: 'valuation-ordered',
                    label: 'Valuation Ordered',
                    onClick: onClickValuationOrdered,
                },
            ] : []),
            ...(showEnterValuationMenuItem ? [
                {
                    key: 'enter-valuation',
                    label: 'Enter Valuation',
                    onClick: onClickEnterValuation,
                },
            ] : []),
            ...(showCancelValuationMenuItem ? [
                {
                    key: 'cancel-valuation',
                    label: 'Cancel',
                    onClick: onClickCancelValuationCallback,
                },
            ] : []),
            ...(showRefuseValuationMenuItem ? [
                {
                    key: 'refuse-valuation',
                    label: 'Mark as Refused',
                    onClick: onClickRefuseValuationCallback,
                },
            ] : []),
        ],
    };

    return (
        <React.Fragment>
            {(!valuation || !valuation.status || valuation.status === ValuerStatusEnum.New) && (
                <Dropdown.Button
                    className='workflow-dropdown-button'
                    menu={workflowMenu}
                    onClick={onClickQuoteRequested}
                    type='primary'
                >
                    Quote Requested
                </Dropdown.Button>
            )}
            {valuation && valuation.status === ValuerStatusEnum.QuoteRequested && (
                <Dropdown.Button
                    className='workflow-dropdown-button'
                    menu={workflowMenu}
                    onClick={onClickEnterQuote}
                    type='primary'
                >
                    Enter Quote
                </Dropdown.Button>
            )}
            {valuation && valuation.status === ValuerStatusEnum.QuoteReceived && (
                <Dropdown.Button
                    className='workflow-dropdown-button'
                    menu={workflowMenu}
                    onClick={onClickValuationOrdered}
                    type='primary'
                >
                    Valuation Ordered
                </Dropdown.Button>
            )}
            {valuation && valuation.status === ValuerStatusEnum.ValuationOrdered && (
                <Dropdown.Button
                    className='workflow-dropdown-button'
                    menu={workflowMenu}
                    onClick={onClickEnterValuation}
                    type='primary'
                >
                    Enter Valuation
                </Dropdown.Button>
            )}
            {valuation && valuation.status === ValuerStatusEnum.ValuationReceived && (
                <Dropdown.Button
                    className='workflow-dropdown-button'
                    menu={workflowMenu}
                    onClick={onClickEnterValuation}
                    type='primary'
                >
                    Update Valuation
                </Dropdown.Button>
            )}

            <EnterQuoteModal
                applicationProperty={applicationProperty}
                isOpen={isQuoteReceivedModalOpen}
                onCancel={onClickEnterQuoteCancel}
                valuer={valuer}
            />
            <EnterValuationModal
                application={application}
                applicationProperty={applicationProperty}
                isOpen={isValuationReceivedModalOpen}
                onCancel={onClickEnterValuationCancel}
                valuer={valuer}
            />
            <QuoteRequestedModal
                applicationProperty={applicationProperty}
                isOpen={isQuoteRequestedModalOpen}
                onCancel={onClickQuoteRequestedCancel}
                propertyValuer={valuation}
                valuer={valuer}
            />
            <ValuationOrderedModal
                applicationProperty={applicationProperty}
                isOpen={isValuationOrderedModalOpen}
                onCancel={onClickValuationOrderedCancel}
                valuer={valuer}
            />
        </React.Fragment>
    );
}
